import * as React from 'react';
import { useState } from 'react';

import { store } from 'Models/Store';
import {
	checkBookingCanBeModified,
	getBookingCancellationRefundCards,
	getBookingCancellationRules,
} from 'Services/Api/_HumanWritten/BookingService/FerryTripBookingService';
import { isBeforeNow } from 'Util/_HumanWritten/GeneralHelpers/DateTimeHelpers';
import alertToast from 'Util/ToastifyUtils';
import { ProcessResults } from 'Views/Components/_HumanWritten/Bookings/BookingDetailedView/Helpers/EditCancelPopUp';
import {
	BookingCancellationInformationDto,
	CancelBookingModal,
} from 'Views/Components/_HumanWritten/Bookings/BookingDetailedView/Modals/CancelBooking/NewCancelBookingModal';

/** What the wizard's departed / checked-in notices are worked out from. */
export interface CancellationNoticeFacts {
	departureDateTime?: Date;
	/** `checkedIn` or a check-in status set. */
	checkedIn: boolean;
	/** The linked leg (return, or the outbound when this is the return) is checked in. */
	linkedLegCheckedIn: boolean;
}

interface UseCancelBookingArgs {
	bookingId: string;
	userFirstName: string;
	userLastName: string;
	bookingLegacyId?: string;
	notices: CancellationNoticeFacts;
	refresh: () => void;
	/** See `CancelBookingModal.onCancelled`. Omit to go to the booking-cancelled page on success. */
	onCancelled?: () => void;
	/** Opened from check-in. Only labels the trip history row. */
	inCheckIn?: boolean;
}

/**
 * Opens the ferry booking cancellation wizard — the one flow behind Cancel on the booking detailed view
 * and in check-in, so both run the same server checks in the same order. Render `modal`; call `trigger`
 * from the cancel action. Guard the action's busy state on `loading` rather than the native `disabled`
 * attribute (see BookingActionButtons).
 */
export function useCancelBooking({
	bookingId,
	userFirstName,
	userLastName,
	bookingLegacyId,
	notices,
	refresh,
	onCancelled,
	inCheckIn,
}: UseCancelBookingArgs) {
	const [loading, setLoading] = useState(false);
	const [cancellationInfo, setCancellationInfo] = useState<BookingCancellationInformationDto | null>(null);
	const [warnings, setWarnings] = useState<string[]>([]);
	const [blockers, setBlockers] = useState<string[]>([]);

	const trigger = async () => {
		if (loading) return;

		// Check the booking can be modified, and fetch the cancellation rules in parallel so the wizard
		// opens instantly with no second loading state once warnings are dismissed.
		setLoading(true);

		try {
			const [res, info] = await Promise.all([
				checkBookingCanBeModified(bookingId),
				getBookingCancellationRules(bookingId, true),
			]);
			const canContinueWithCancel = await ProcessResults(res, true);

			setLoading(false);

			if (!canContinueWithCancel) {
				return;
			}

			const collected = collectCancellationNotices(notices);
			setWarnings(collected.warnings);
			setBlockers(collected.blockers);
			// The server sends `cardsToRefund: null` here — the cards come from the request below.
			// Null reads as "no cards" to the wizard, which then labels a card refund "Manual
			// refund" until they land, so it is cleared to undefined ("not fetched yet").
			setCancellationInfo({ ...info, cardsToRefund: undefined });

			/*
				The refundable-card breakdown, fetched after the modal is already up.

				It is the only part of the quote that calls the payment provider, and it cost
				~11.6s of an 11.7s response on a hosted environment while every other call on the
				page answered in ~45ms. The wizard does not read it until its confirm step, so it
				has the whole of steps 1 and 2 to arrive.

				Merged rather than assigned: `cardsToRefund === undefined` is how the wizard knows
				the cards were never fetched (the event and parking flows build their DTO without
				them) and skips the breakdown instead of drawing a wrong one. Leaving it undefined
				until this lands keeps that reading true in the gap.
			*/
			getBookingCancellationRefundCards(bookingId)
				.then(cards =>
					setCancellationInfo(current => (current ? { ...current, cardsToRefund: cards } : current)),
				)
				.catch(error => {
					// Never surfaced. The cancellation works without it — the breakdown only says
					// which card the money would land on.
					console.error('Could not load the refundable-card breakdown:', error);
				});
		} catch (error) {
			setLoading(false);
			console.error('Error checking booking modification:', error);
			alertToast('Could not load cancellation details', 'error');
		}
	};

	const close = () => setCancellationInfo(null);

	const modal = cancellationInfo && (
		<CancelBookingModal
			bookingId={bookingId}
			userFirstName={userFirstName}
			userLastName={userLastName}
			refresh={refresh}
			bookingLegacyId={bookingLegacyId ?? ''}
			cancellationInfo={cancellationInfo}
			warnings={warnings}
			blockers={blockers}
			onClose={close}
			inCheckIn={inCheckIn}
			onCancelled={
				onCancelled &&
				(() => {
					close();
					onCancelled();
				})
			}
		/>
	);

	return { trigger, loading, modal };
}

// Splits departed/checked-in notices into two buckets:
// - `blockers` — issues that should stop the customer from completing the cancellation
//   (the customer can't cancel a departed or checked-in booking themselves; staff can,
//   so the same conditions stay informational for staff).
// - `warnings` — informational only, never blocking. Includes the return-booking
//   checked-in case: the backend leaves a checked-in return leg out of the cancellable list
//   for everyone, staff included, so there is nothing to "block" — the notice just explains
//   why the return isn't listed.
function collectCancellationNotices(facts: CancellationNoticeFacts): { warnings: string[]; blockers: string[] } {
	const warnings: string[] = [];
	const blockers: string[] = [];
	const departed = !!facts.departureDateTime && isBeforeNow(facts.departureDateTime);

	if (departed) {
		const msg = 'This booking has already departed.';
		if (store.isStaff) warnings.push(msg);
		else blockers.push(msg);
	}
	if (facts.checkedIn) {
		const msg = 'This booking has already been checked in.';
		if (store.isStaff) warnings.push(msg);
		else blockers.push(msg);
	}
	if (facts.linkedLegCheckedIn) {
		warnings.push('The linked return booking has already been checked in, so it cannot be cancelled.');
	}

	return { warnings, blockers };
}
